<!-- ============================================================================
     SUBSCRIPTION PLAN CONFIGURATOR - SHARED COMPONENT
     A reusable component for selecting and configuring subscription plans
     ============================================================================ -->

<div class="subscription-plan-configurator-container">
  <!-- ========================================================================
       SECTION 1: APPLICATION SELECTION
       ======================================================================== -->
  <div class="configuration-section" *ngIf="showApplicationSelectionStep">
    <div class="section-header-simple">
      <div class="section-number" [class.active]="hasSelectedApplications">1</div>
      <div class="section-info">
        <h6 class="section-title-simple">
          Select Applications
          <span
            class="badge bg-success ms-2"
            *ngIf="areApplicationsConfirmedAndLocked"
            style="font-size: 11px; font-weight: 500"
          >
            ✓ {{ selectedApplicationsCount }} app{{ selectedApplicationsCount !== 1 ? 's' : '' }}
            selected
          </span>
        </h6>
        <p class="section-desc-simple">
          <span *ngIf="!areApplicationsConfirmedAndLocked"
            >Choose the applications to include in your subscription</span
          >
          <span *ngIf="areApplicationsConfirmedAndLocked" style="color: #10b981; font-weight: 500">
            ✓ Applications selected. Plans loaded below.
          </span>
        </p>
      </div>
      <div class="section-actions" *ngIf="areApplicationsConfirmedAndLocked">
        <button
          class="btn btn-outline-secondary btn-sm"
          type="button"
          (click)="unlockAndEditApplicationSelection()"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          Edit Apps
        </button>
        <button
          class="btn btn-outline-danger btn-sm"
          type="button"
          (click)="clearAllApplicationSelections()"
          *ngIf="allowResetConfiguration"
        >
          <app-icon name="close" width="16" height="16"></app-icon>
          Clear All
        </button>
      </div>
    </div>

    <!-- Application Selection Grid -->
    <div
      class="applications-grid-simple"
      [class.locked]="areApplicationsConfirmedAndLocked"
      [style.pointer-events]="areApplicationsConfirmedAndLocked ? 'none' : 'auto'"
      [style.opacity]="areApplicationsConfirmedAndLocked ? '0.7' : '1'"
    >
      <div
        class="application-card-simple"
        *ngFor="let app of workingApplicationsList; trackBy: trackByApplicationId"
        [class.selected]="app.isAppSelect"
        (click)="!areApplicationsConfirmedAndLocked && toggleApplicationSelection(app)"
      >
        <div class="app-select-checkbox">
          <input
            type="checkbox"
            [checked]="app.isAppSelect"
            [disabled]="areApplicationsConfirmedAndLocked"
            (click)="$event.stopPropagation()"
            (change)="
              $event.stopPropagation();
              !areApplicationsConfirmedAndLocked && toggleApplicationSelection(app)
            "
          />
        </div>
        <img
          [src]="app.appIcon || 'assets/images/navigation/default-app.svg'"
          alt="{{ app.name }}"
          class="application-icon-simple"
        />
        <h6 class="application-name-simple">{{ app.name }}</h6>
      </div>
    </div>

    <!-- Confirm Apps Button -->
    <div class="section-footer-actions" *ngIf="!areApplicationsConfirmedAndLocked">
      <button
        class="btn btn-primary btn-continue-simple"
        [disabled]="!hasSelectedApplications || isLoadingPlanPackages"
        (click)="confirmApplicationSelectionAndLoadPlans()"
      >
        <span *ngIf="!isLoadingPlanPackages">
          Continue to Plans
          <app-icon name="arrow-right" width="16" height="16"></app-icon>
        </span>
        <span *ngIf="isLoadingPlanPackages">
          <span class="spinner-border spinner-border-sm me-1"></span>
          Loading Plans...
        </span>
      </button>
    </div>
  </div>

  <!-- ========================================================================
       SECTION 2: PLAN SELECTION
       ======================================================================== -->
  <div class="configuration-section" [class.section-disabled]="!areApplicationsConfirmedAndLocked">
    <div class="section-header-simple">
      <div class="section-number" [class.active]="selectedPlanPackage?.id">{{ showApplicationSelectionStep ? 2 : 1 }}</div>
      <div class="section-info">
        <h6 class="section-title-simple">
          {{ showApplicationSelectionStep ? 'Choose Subscription Plan' : 'Company Subscription' }}
          <span
            class="badge bg-success ms-2"
            *ngIf="selectedPlanPackage?.id"
            style="font-size: 11px; font-weight: 500"
          >
            ✓ Plan Selected
          </span>
        </h6>
        <p class="section-desc-simple">
          <span *ngIf="!selectedPlanPackage?.id"
            >Select the plan that fits your organization's needs</span
          >
          <span *ngIf="selectedPlanPackage?.id" style="color: #10b981; font-weight: 500">
            ✓ {{ selectedPlanPackage?.name }} selected
          </span>
        </p>
      </div>
      <div class="section-actions">
        <button
          class="btn btn-outline-secondary btn-sm"
          type="button"
          (click)="resetPlanSelectionOnly()"
          *ngIf="allowResetConfiguration && selectedPlanPackage?.id"
        >
          <app-icon
            name="refresh"
            width="16"
            height="16"
            style="margin-right: 4px; vertical-align: text-bottom"
          ></app-icon>
          Reset Plan
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div class="loading-state-simple" *ngIf="isLoadingPlanPackages">
      <div class="spinner-border text-primary" role="status"></div>
      <p>Loading available subscription plans...</p>
    </div>

    <!-- Plan Packages Grid -->
    <div
      class="plans-grid-simple"
      *ngIf="
        !lockPlanSelection &&
        !isLoadingPlanPackages &&
        areApplicationsConfirmedAndLocked &&
        availablePlanPackages.length > 0
      "
    >
      <div
        class="modern-plan-card"
        *ngFor="let planPackage of availablePlanPackages; trackBy: trackByPlanPackageId"
        [class.selected]="planPackage.isAssigned"
        [class.locked]="lockPlanSelection"
        (click)="!lockPlanSelection && selectPlanPackage(planPackage)"
      >
        <!-- HEADER -->
        <div class="modern-card-header">
          <div class="modern-header-top">
            <div>
              <div class="modern-plan-name">{{ planPackage.name }}</div>
              <div class="modern-plan-subtitle" *ngIf="planPackage.description">
                {{ planPackage.description }}
              </div>
            </div>
            <div class="modern-price-block d-flex flex-column align-items-end">
              <div class="modern-price-original" *ngIf="getPlanHasDiscount(planPackage)">
                {{ getPlanOriginalPrice(planPackage) | currency }}
              </div>
              <div class="d-flex align-items-end">
                <div class="modern-price-main">
                  {{ getPlanDisplayPrice(planPackage) | currency }}
                </div>
                <div class="modern-price-period ps-1">{{ getBillingPeriodLabel() }}</div>
              </div>
            </div>
          </div>
          <div class="modern-plan-badges">
            <span class="modern-badge modern-badge-blue"
              >{{ planPackage.apps.length || 0 }} App{{
                planPackage.apps.length !== 1 ? 's' : ''
              }}</span
            >
            <span class="modern-badge modern-badge-green" *ngIf="planPackage.isAssigned"
              >Selected</span
            >
            <span
              class="modern-badge modern-badge-violet"
              *ngIf="planPackage.addons && planPackage.addons.length > 0"
            >
              {{ planPackage.addons.length }} Add-on{{ planPackage.addons.length !== 1 ? 's' : '' }}
            </span>
            <span
              class="modern-badge modern-badge-discount"
              *ngIf="getPlanHasDiscount(planPackage)"
            >
              {{ getPlanDiscountPercent(planPackage) | number: '1.0-2' }}% off
            </span>
          </div>
        </div>

        <!-- APPS SECTION -->
        <div class="modern-section-label">App Access &amp; Pricing</div>
        <div class="modern-app-list">
          <div
            class="modern-app-row"
            *ngFor="let app of getSortedApps(planPackage.apps); trackBy: trackById"
          >
            <img
              [src]="getApplicationIcon(app.appId)"
              alt=""
              class="modern-app-icon"
              [title]="app.appName"
            />
            <div class="modern-app-info">
              <div class="modern-app-name">{{ app.appName }}</div>
              <div class="modern-app-seats-row">
                <span class="modern-seats-chip" *ngIf="hasPerSeatCharge(app)">
                  <app-icon
                    name="users"
                    width="12"
                    height="12"
                    [attr.stroke]="'#64748B'"
                    [attr.stroke-width]="'1.8'"
                  ></app-icon>
                  {{ app.includedSeats }} seat{{ app.includedSeats > 1 ? 's' : '' }} included
                </span>
                <!-- Show revenue sharing or per-seat pricing conditionally -->
                <span
                  class="modern-extra-seat-tag"
                  *ngIf="!isRevenueSharing(app) && app.perSeatPrice > 0"
                >
                  +{{ app.perSeatPrice | currency }}/seat
                </span>
                <span
                  class="modern-extra-seat-tag"
                  *ngIf="isRevenueSharing(app)"
                  style="background: #dcfce7; color: #166534"
                >
                  {{ getRevenueSharingPercentage(app) }}% of revenue
                </span>
              </div>
            </div>
            <div class="modern-app-price-block">
              <div class="modern-app-base-price">{{ app.basePrice | currency }}</div>
              <div class="modern-app-price-label">base{{ getBillingPeriodLabel() }}</div>
            </div>
          </div>
        </div>

        <!-- ADDONS & PRICING Section -->
        <ng-container *ngIf="planPackage.addons && planPackage.addons.length > 0">
          <div class="modern-divider"></div>
          <div class="modern-section-label">Addons &amp; Pricing</div>
          <div class="modern-addon-list">
            <div
              class="modern-app-row align-items-start"
              *ngFor="let addon of getSortedAddons(planPackage.addons); trackBy: trackById"
            >
              <!-- Addon Icon -->
              <div class="modern-addon-icon-wrap">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  stroke-width="3"
                  stroke-linecap="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </div>
              <!-- Addon Info -->
              <div class="modern-app-info">
                <div class="modern-app-name">{{ addon.name }}</div>
                <div class="modern-app-seats-row pb-1">
                  <span class="modern-seats-chip" *ngIf="addon.includedSeats > 0">
                    <app-icon
                      name="users"
                      width="12"
                      height="12"
                      [attr.stroke]="'#64748B'"
                      [attr.stroke-width]="'1.8'"
                    ></app-icon>
                    {{ addon.includedSeats }} seat{{ addon.includedSeats !== 1 ? 's' : '' }}
                    included
                  </span>
                  <span class="modern-extra-seat-tag addon-seat-tag" *ngIf="addon.perSeatPrice > 0">
                    +{{ addon.perSeatPrice | currency }}/seat
                  </span>
                </div>
                <!-- Addon Prices (all charges) -->
                <div class="row" style="align-items: start; text-align: left">
                  <div
                    class="col-md-6 mt-1"
                    *ngFor="let charge of getAddonAllChargesForDisplay(addon)"
                  >
                    <span class="modern-app-base-price addon-base-price">{{ charge.value }}</span>
                    <span class="modern-app-price-label">{{ charge.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- Active Plan Display (Manage Add-ons mode - horizontal) -->
    <ng-container *ngIf="lockPlanSelection && !isLoadingPlanPackages">
      <ng-container *ngIf="selectedPlanPackage; let plan">
        <div class="active-plan-horizontal">
          <!-- Plan Identity -->
          <div class="active-plan-identity">
            <div class="active-plan-status-badge">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Active Plan
            </div>
            <div class="active-plan-name">{{ plan.name }}</div>
            <div class="active-plan-desc" *ngIf="plan.description">{{ plan.description }}</div>
            <div class="active-plan-meta-badges">
              <span class="modern-badge modern-badge-blue">
                {{ plan.apps.length }} App{{ plan.apps.length !== 1 ? 's' : '' }}
              </span>
              <span
                class="modern-badge modern-badge-violet"
                *ngIf="plan.addons && plan.addons.length > 0"
              >
                {{ plan.addons.length }} Add-on{{ plan.addons.length !== 1 ? 's' : '' }}
              </span>
              <span class="modern-badge modern-badge-discount" *ngIf="getPlanHasDiscount(plan)">
                {{ getPlanDiscountPercent(plan) | number: '1.0-2' }}% off
              </span>
            </div>
          </div>

          <!-- Apps Included -->
          <div class="active-plan-apps-section">
            <div class="active-plan-section-label">Apps Included</div>
            <div class="active-plan-app-chips">
              <div
                class="active-app-chip"
                *ngFor="let app of getSortedApps(plan.apps || []); trackBy: trackById"
              >
                <img [src]="getApplicationIcon(app.appId)" alt="" class="active-app-chip-icon" />
                <div class="active-app-chip-info">
                  <span class="active-app-chip-name">{{ app.appName }}</span>
                  <span class="active-app-chip-price"
                    >{{ app.basePrice | currency }}{{ getBillingPeriodLabel() }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Price -->
          <div class="active-plan-price-section">
            <div class="active-plan-section-label">Plan Price</div>
            <div class="active-plan-price-original" *ngIf="getPlanHasDiscount(plan)">
              {{ getPlanOriginalPrice(plan) | currency }}
            </div>
            <div class="active-plan-price-main">
              {{ getPlanDisplayPrice(plan) | currency }}
            </div>
            <div class="active-plan-price-period">{{ getBillingPeriodLabel() }}</div>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <!-- Empty State -->
    <div
      class="empty-state-simple"
      *ngIf="
        !lockPlanSelection &&
        !isLoadingPlanPackages &&
        areApplicationsConfirmedAndLocked &&
        availablePlanPackages.length === 0
      "
    >
      <app-icon
        name="box"
        width="48"
        height="48"
        [attr.stroke]="'#cbd5e1'"
        strokeWidth="1.5"
      ></app-icon>
      <p>No subscription plans available for the selected applications</p>
    </div>
  </div>

  <!-- ========================================================================
       SECTION 3: ADD-ONS (OPTIONAL)
       ======================================================================== -->
  <div class="configuration-section" [class.section-disabled]="!selectedPlanPackage?.id">
    <div class="section-header-simple">
      <div class="section-number" [class.active]="selectedPlanPackage?.id">{{ showApplicationSelectionStep ? 3 : 2 }}</div>
      <div class="section-info">
        <h6 class="section-title-simple">Add-ons <span class="optional-badge">(Optional)</span></h6>
        <p class="section-desc-simple">
          Enhance your subscription with add-on services
          <span *ngIf="selectedAddOnsWithQuantities.size > 0" class="text-success">
            ({{ selectedAddOnsWithQuantities.size }} selected)
          </span>
        </p>
      </div>
      <div class="section-actions" *ngIf="selectedAddOnsWithQuantities.size > 0">
        <button class="btn btn-outline-danger btn-sm" type="button" (click)="clearAllAddOns()">
          Clear Add-ons
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div class="loading-state-simple" *ngIf="isLoadingAddOnServices">
      <div class="spinner-border text-primary spinner-border-sm" role="status"></div>
      <span>Loading add-ons...</span>
    </div>

    <!-- Add-ons Grid -->
    <div
      class="addons-grid-simple"
      *ngIf="
        !isLoadingAddOnServices && selectedPlanPackage?.id && availableAddOnServices.length > 0
      "
    >
      <div
        class="addon-card-simple"
        *ngFor="let addOn of availableAddOnServices; trackBy: trackByAddOnId"
        [class.selected]="isAddOnSelected(addOn.id)"
        (click)="toggleAddOnSelection(addOn)"
      >
        <!-- Add-on Header -->
        <div class="addon-header-simple">
          <input
            type="checkbox"
            class="addon-checkbox-simple"
            [checked]="isAddOnSelected(addOn.id)"
            (click)="$event.stopPropagation()"
            (change)="$event.stopPropagation(); toggleAddOnSelection(addOn)"
          />
          <div class="addon-info-simple pe-0">
            <h6 class="addon-name-simple">{{ addOn.name }}</h6>
            <p class="addon-desc-simple mb-1" *ngIf="addOn.description">{{ addOn.description }}</p>
            <!-- Price Corner: all charges -->
            <div
              class="row"
              *ngIf="addOn.billableServiceCharges && addOn.billableServiceCharges.length > 0"
            >
              <div
                class="col-md-6"
                *ngFor="let charge of getAddOnAllChargesForDisplay(addOn)"
              >
                <span class="price-amount-corner">{{ charge.value }}</span>
                <span class="price-period-corner">{{ charge.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      class="empty-state-simple"
      *ngIf="
        !isLoadingAddOnServices && selectedPlanPackage?.id && availableAddOnServices.length === 0
      "
    >
      <app-icon
        name="alert-circle"
        width="48"
        height="48"
        [attr.stroke]="'#cbd5e1'"
        strokeWidth="1.5"
      ></app-icon>
      <p>No add-ons available for selected apps</p>
    </div>
  </div>

  <!-- ========================================================================
       SECTION 4: SEAT CONFIGURATION
       ======================================================================== -->
  <div
    class="configuration-section seat-configuration-section"
    [class.section-disabled]="!selectedPlanPackage.id"
    *ngIf="selectedPlanPackage && hasAdditionalSeatPricing(selectedPlanPackage)"
  >
    <div class="section-header-simple">
      <div class="section-number" [class.active]="selectedPlanPackage.id">{{ showApplicationSelectionStep ? 4 : 3 }}</div>
      <div class="section-info">
        <h6 class="section-title-simple">Configure Seats</h6>
        <p class="section-desc-simple">Adjust the number of seats for applications and add-ons</p>
      </div>
    </div>

    <!-- Seat Configuration Card -->
    <div class="seat-config-card" *ngIf="selectedPlanPackage.id">
      <div class="seat-app-list">
        <!-- Apps with per-seat charges -->
        <div
          class="seat-app-item"
          *ngFor="let app of getSortedApps(selectedPlanPackage.apps || []); trackBy: trackById"
          [hidden]="!hasPerSeatCharge(app)"
        >
          <div class="seat-app-info">
            <label class="seat-app-label">{{ app.appName }}</label>
            <div class="seat-app-details">
              <span class="seat-app-base-price"
                >Base: {{ app.basePrice | currency }}{{ getBillingPeriodLabel() }}</span
              >
              <span class="seat-app-included" *ngIf="hasPerSeatCharge(app)"
                >{{ app.includedSeats }} seats included</span
              >
              <!-- Show per-seat price or revenue sharing conditionally -->
              <span class="seat-app-price" *ngIf="!isRevenueSharing(app) && app.perSeatPrice > 0">
                +{{ app.perSeatPrice | currency }}/seat{{ getBillingPeriodLabel() }}
              </span>
              <span class="seat-app-price" *ngIf="isRevenueSharing(app)" style="color: #059669">
                {{ getRevenueSharingPercentage(app) }}% of revenue
              </span>
            </div>
          </div>
          <div class="seat-app-input">
            <label class="input-label">Seats</label>
            <input
              type="number"
              class="form-control seat-input"
              [(ngModel)]="app.currentSeats"
              [min]="app.includedSeats"
              (blur)="updateSeatsForApplication(app.appId, app.currentSeats)"
              placeholder="Seats"
            />
          </div>
        </div>

        <!-- Add-ons with per-seat charges -->
        <div
          class="seat-app-item"
          *ngFor="
            let addon of getSortedAddons(selectedPlanPackage.addons || []);
            trackBy: trackById
          "
          [hidden]="!hasPerSeatChargeAddon(addon)"
        >
          <div class="seat-app-info">
            <label class="seat-app-label">{{ addon.name }} (Add-on)</label>
            <div class="seat-app-details">
              <span class="seat-app-base-price"
                >Base: {{ addon.price | currency }}{{ getBillingPeriodLabel() }}</span
              >
              <span class="seat-app-included" *ngIf="hasPerSeatChargeAddon(addon)"
                >{{ addon.includedSeats }} seats included</span
              >
              <span class="seat-app-price" *ngIf="addon.perSeatPrice > 0">
                +{{ addon.perSeatPrice | currency }}/seat{{ getBillingPeriodLabel() }}
              </span>
            </div>
          </div>
          <div class="seat-app-input">
            <label class="input-label">Seats</label>
            <input
              type="number"
              class="form-control seat-input"
              [(ngModel)]="addon.currentSeats"
              [min]="addon.includedSeats"
              (blur)="updateSeatsForAddon(addon.id, addon.currentSeats)"
              placeholder="Seats"
            />
          </div>
        </div>
      </div>

      <!-- Price Breakdown (if enabled) -->
      <div class="price-breakdown-box" *ngIf="showDetailedPricingBreakdown">
        <h6 class="breakdown-title">Price Breakdown</h6>

        <!-- Detailed Charges for Apps -->
        <ng-container
          *ngFor="let appCharges of getDetailedChargeBreakdownByApplication(); trackBy: trackById"
        >
          <ng-container *ngFor="let charge of appCharges.charges; trackBy: trackById">
            <div class="breakdown-item" [style.opacity]="charge.includedInTotal ? '1' : '0.6'">
              <div class="breakdown-label">
                <p class="mb-0">
                  {{ appCharges.appName }}
                  <span
                    class="ms-1"
                    *ngIf="!charge.includedInTotal"
                    style="font-size: 10px; color: #f59e0b; font-weight: 500"
                    >(At Actual)</span
                  >
                </p>
                <p class="mb-0" style="font-size: 11px">
                  {{ charge.typeName }} {{ formatChargeValue(charge.value, charge.type) }}
                </p>
              </div>
              <span
                class="breakdown-value"
                [style.text-decoration]="charge.includedInTotal ? 'none' : 'line-through'"
                >{{
                  charge.includedInTotal
                    ? formatChargeValue(charge.value, charge.type)
                    : (0 | currency)
                }}</span
              >
            </div>
          </ng-container>
        </ng-container>

        <!-- Detailed Charges for Addons -->
        <ng-container
          *ngFor="let addonCharges of getDetailedChargeBreakdownByAddon(); trackBy: trackById"
        >
          <ng-container *ngFor="let charge of addonCharges.charges; trackBy: trackById">
            <div class="breakdown-item" [style.opacity]="charge.includedInTotal ? '1' : '0.6'">
              <div class="breakdown-label">
                <p class="mb-0">
                  {{ addonCharges.addonName }} (Add-on)
                  <span
                    class="ms-1"
                    *ngIf="!charge.includedInTotal"
                    style="font-size: 10px; color: #f59e0b; font-weight: 500"
                    >(At Actual)</span
                  >
                </p>
                <p class="mb-0" style="font-size: 11px">
                  {{ charge.typeName }} {{ formatChargeValue(charge.value, charge.type) }}
                </p>
              </div>
              <span
                class="breakdown-value"
                [style.text-decoration]="charge.includedInTotal ? 'none' : 'line-through'"
                >{{
                  charge.includedInTotal
                    ? formatChargeValue(charge.value, charge.type)
                    : (0 | currency)
                }}</span
              >
            </div>
          </ng-container>
        </ng-container>

        <!-- Additional Seats -->
        <ng-container
          *ngFor="
            let appBreakdown of getAdditionalSeatsBreakdownByApplication();
            trackBy: trackById
          "
        >
          <div class="breakdown-item">
            <span class="breakdown-label">
              {{ appBreakdown.appName }} - Additional Seats ({{ appBreakdown.additionalSeats }} ×
              {{ appBreakdown.perSeatPrice | currency }}{{ getBillingPeriodLabel() }})
            </span>
            <span class="breakdown-value">{{ appBreakdown.totalCost | currency }}</span>
          </div>
        </ng-container>

        <!-- Additional Seats for Add-ons -->
        <ng-container
          *ngFor="let addonBreakdown of getAdditionalSeatsBreakdownByAddon(); trackBy: trackById"
        >
          <div class="breakdown-item">
            <span class="breakdown-label">
              {{ addonBreakdown.addonName }} (Add-on) - Additional Seats ({{
                addonBreakdown.additionalSeats
              }}
              × {{ addonBreakdown.perSeatPrice | currency }}{{ getBillingPeriodLabel() }})
            </span>
            <span class="breakdown-value">{{ addonBreakdown.totalCost | currency }}</span>
          </div>
        </ng-container>

        <!-- Subtotal (before discount) -->
        <div class="breakdown-divider" *ngIf="calculateSavingsInformation()"></div>
        <div class="breakdown-item" *ngIf="calculateSavingsInformation()">
          <span class="breakdown-label">Subtotal</span>
          <span class="breakdown-value">{{ calculateSubtotal() | currency }}</span>
        </div>

        <!-- Discount (if any) -->
        <div class="breakdown-item breakdown-discount" *ngIf="calculateSavingsInformation() as savings">
          <span class="breakdown-label">
            Plan Discount
            ({{ savings.isPercentage ? savings.savedValue + '%' : ('$' + savings.savedValue.toFixed(2)) }} off)
          </span>
          <span class="breakdown-value">-{{ savings.savedAmount | currency }}</span>
        </div>

        <div class="breakdown-divider"></div>

        <!-- Total -->
        <div class="breakdown-item breakdown-total">
          <span class="breakdown-label"><strong>Total Price</strong></span>
          <span class="breakdown-value"
            ><strong>{{ calculateFinalTotalPrice() | currency }}</strong></span
          >
        </div>
        <p class="billing-period-note">
          /{{ currentBillingCycle === BillingCycle.Yearly_PaidYearly ? 'year' : 'month' }}
        </p>
      </div>
    </div>
  </div>

  <!-- ========================================================================
       SECTION 5: TRIAL PERIOD CONFIGURATION
       ======================================================================== -->
  <div
    class="configuration-section"
    [class.section-disabled]="!selectedPlanPackage?.id"
    *ngIf="selectedPlanPackage?.id && enableTrialConfiguration"
  >
    <div class="section-header-simple">
      <div class="section-number" [class.active]="selectedPlanPackage?.id">{{ showApplicationSelectionStep ? 5 : 4 }}</div>
      <div class="section-info">
        <h6 class="section-title-simple">
          Trial Period
          <span
            class="badge ms-2"
            [class.bg-warning]="isTrialEnabled"
            [class.bg-secondary]="!isTrialEnabled"
            *ngIf="selectedPlanPackage?.id"
            style="font-size: 11px; font-weight: 500"
          >
            {{ isTrialEnabled ? 'Enabled' : 'Optional' }}
          </span>
        </h6>
        <p class="section-desc-simple">
          <span *ngIf="!isTrialEnabled">Optional: Enable trial period for this subscription</span>
          <span *ngIf="isTrialEnabled" style="color: #f59e0b; font-weight: 500">
            Trial ends {{ trialEndDate | date: 'MMM d, yyyy' }}
          </span>
        </p>
      </div>
    </div>

    <!-- Trial Period Configuration -->
    <div class="trial-configuration-section">
      <div class="trial-header">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            id="enableTrial"
            [(ngModel)]="isTrialEnabled"
            (change)="toggleTrialPeriod(isTrialEnabled)"
          />
          <label class="form-check-label" for="enableTrial">
            <strong>Enable Trial Period</strong>
            <span class="trial-description"
              >Allow customers to try the subscription before committing</span
            >
          </label>
        </div>
      </div>

      <div class="trial-options" *ngIf="isTrialEnabled">
        <div class="trial-duration-config">
          <label for="trialDuration" class="trial-label">Trial Duration</label>
          <div class="trial-duration-input-group">
            <input
              type="number"
              id="trialDuration"
              class="form-control form-control-sm"
              [(ngModel)]="trialDurationDays"
              (ngModelChange)="updateTrialDuration($event)"
              min="1"
              max="90"
              style="width: 80px"
            />
            <span class="trial-duration-label">days</span>
          </div>
        </div>

        <div class="trial-end-info">
          <div class="trial-badge">
            <app-icon name="clock" width="14" height="14"></app-icon>
            <span
              >Trial ends on: <strong>{{ trialEndDate | date: 'mediumDate' }}</strong></span
            >
          </div>
          <div class="trial-note">
            <app-icon name="info" width="14" height="14"></app-icon>
            <span
              >Subscription status will be set to <strong>Trial</strong> until
              {{ trialEndDate | date: 'MMM d, yyyy' }}</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ========================================================================
       SECTION 6: PRICING SUMMARY (FINAL)
       ======================================================================== -->
  <div
    class="configuration-section pricing-summary-section"
    *ngIf="selectedPlanPackage?.id && showDetailedPricingBreakdown"
  >
    <div class="section-header-simple">
      <div class="section-number completed">
        <app-icon
          name="checkmark"
          width="16"
          height="16"
          [attr.stroke]="'white'"
          strokeWidth="3"
        ></app-icon>
      </div>
      <div class="section-info">
        <h6 class="section-title-simple">Pricing Summary</h6>
        <p class="section-desc-simple">Final cost breakdown for your subscription</p>
      </div>
      <div class="section-actions">
        <!-- Billing Cycle Selector -->
        <div class="billing-cycle-mini" style="display: flex; gap: 8px; align-items: center">
          <label for="billingCycleSelect" style="margin: 0; font-weight: 500; white-space: nowrap"
            >Billing Cycle:</label
          >
          <select
            id="billingCycleSelect"
            class="form-select form-select-sm"
            [(ngModel)]="currentBillingCycle"
            (change)="changeBillingCycle(currentBillingCycle)"
            style="width: auto; min-width: 150px"
            [disabled]="lockPlanSelection"
          >
            <option [value]="BillingCycle.Monthly">Monthly</option>
            <option [value]="BillingCycle.Yearly_PaidMonthly">Yearly (Paid Monthly)</option>
            <option [value]="BillingCycle.Yearly_PaidYearly">Yearly (Paid Yearly)</option>
          </select>
        </div>
      </div>
    </div>

    <div class="pricing-summary-card">
      <!-- Subscription Details -->
      <div class="summary-section-clean">
        <h6 class="summary-title-clean">Subscription Details</h6>
        <div class="summary-grid">
          <div class="summary-row">
            <span class="summary-key">Selected Plan</span>
            <span class="plan-name-val">{{ selectedPlanPackage?.name }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-key">Billing Cycle</span>
            <span class="summary-val">{{ getBillingCycleDisplayName() }}</span>
          </div>
          <div
            class="summary-row"
            *ngIf="selectedPlanPackage && hasAdditionalSeatPricing(selectedPlanPackage)"
          >
            <span class="summary-key">Total Seats</span>
            <span class="summary-val">{{ getTotalSeatsAcrossAllApplications() }}</span>
          </div>
        </div>
      </div>

      <!-- Seat Configuration -->
      <div
        class="summary-section-clean"
        *ngIf="selectedPlanPackage && hasAdditionalSeatPricing(selectedPlanPackage)"
      >
        <h6 class="summary-title-clean">Seat Configuration</h6>
        <div>
          <!-- Apps with seats -->
          <div
            class="detailed-app-seat-row"
            *ngFor="let app of getSortedApps(selectedPlanPackage.apps || []); trackBy: trackById"
            [hidden]="!hasPerSeatCharge(app)"
          >
            <img [src]="getApplicationIcon(app.appId)" alt="" class="detailed-app-icon" />
            <span class="detailed-app-name">{{ app.appName }}</span>
            <span class="detailed-seat-badge">{{ app.includedSeats }} included</span>
            <span class="detailed-seat-value">{{ app.currentSeats }} seat(s)</span>
          </div>
          <!-- Addons with seats -->
          <div
            class="detailed-app-seat-row"
            *ngFor="
              let addon of getSortedAddons(selectedPlanPackage.addons || []);
              trackBy: trackById
            "
            [hidden]="!hasPerSeatChargeAddon(addon)"
          >
            <span class="detailed-app-name"
              >{{ addon.name }} <span style="font-size: 11px; opacity: 0.7">(Add-on)</span></span
            >
            <span class="detailed-seat-badge">{{ addon.includedSeats }} included</span>
            <span class="detailed-seat-value">{{ addon.currentSeats }} seat(s)</span>
          </div>
        </div>
      </div>

      <!-- Cost Breakdown -->
      <div class="summary-section-clean">
        <h6 class="summary-title-clean">Cost Breakdown</h6>
        <div>
          <!-- Detailed Charges for Each App -->
          <ng-container
            *ngFor="let appCharges of getDetailedChargeBreakdownByApplication(); trackBy: trackById"
          >
            <ng-container *ngFor="let charge of appCharges.charges; trackBy: trackById">
              <div class="detailed-cost-row" [style.opacity]="charge.includedInTotal ? '1' : '0.6'">
                <div class="detailed-cost-left">
                  <div class="detailed-cost-name">
                    {{ appCharges.appName }} — {{ charge.typeName }}
                    <span class="detailed-cost-type-tag tag-base">App</span>
                    <span
                      *ngIf="!charge.includedInTotal"
                      class="detailed-cost-type-tag"
                      style="background-color: #f59e0b; color: white"
                      >At Actual</span
                    >
                  </div>
                  <div class="detailed-cost-formula">
                    {{ charge.typeName }} charge: {{ formatChargeValue(charge.value, charge.type)
                    }}<span *ngIf="charge.type !== ChargeType.PercentageOfRevenue">{{
                      getBillingPeriodLabel()
                    }}</span>
                  </div>
                </div>
                <div
                  class="detailed-cost-amount"
                  [style.text-decoration]="charge.includedInTotal ? 'none' : 'line-through'"
                >
                  {{
                    charge.includedInTotal
                      ? formatChargeValue(charge.value, charge.type)
                      : (0 | currency)
                  }}
                </div>
              </div>
            </ng-container>
          </ng-container>

          <!-- Detailed Charges for Each Addon -->
          <ng-container
            *ngFor="let addonCharges of getDetailedChargeBreakdownByAddon(); trackBy: trackById"
          >
            <ng-container *ngFor="let charge of addonCharges.charges; trackBy: trackById">
              <div class="detailed-cost-row" [style.opacity]="charge.includedInTotal ? '1' : '0.6'">
                <div class="detailed-cost-left">
                  <div class="detailed-cost-name">
                    {{ addonCharges.addonName }} — {{ charge.typeName }}
                    <span class="detailed-cost-type-tag tag-addon">Add-on</span>
                    <span
                      *ngIf="!charge.includedInTotal"
                      class="detailed-cost-type-tag"
                      style="background-color: #f59e0b; color: white"
                      >At Actual</span
                    >
                  </div>
                  <div class="detailed-cost-formula">
                    {{ charge.typeName }} charge: {{ formatChargeValue(charge.value, charge.type)
                    }}<span *ngIf="charge.type !== ChargeType.PercentageOfRevenue">
                      <span *ngIf="charge.type == ChargeType.PerTransaction">/transaction</span>
                      {{ getBillingPeriodLabel() }}</span
                    >
                  </div>
                </div>
                <div
                  class="detailed-cost-amount"
                  [style.text-decoration]="charge.includedInTotal ? 'none' : 'line-through'"
                >
                  {{
                    charge.includedInTotal
                      ? formatChargeValue(charge.value, charge.type)
                      : (0 | currency)
                  }}
                </div>
              </div>
            </ng-container>
          </ng-container>

          <!-- Additional Seats for Each App -->
          <ng-container
            *ngFor="
              let appBreakdown of getAdditionalSeatsBreakdownByApplication();
              trackBy: trackById
            "
          >
            <div class="detailed-cost-row">
              <div class="detailed-cost-left">
                <div class="detailed-cost-name">
                  {{ appBreakdown.appName }} — Additional Seats
                  <span class="detailed-cost-type-tag tag-seat">Seats</span>
                </div>
                <div class="detailed-cost-formula">
                  {{ appBreakdown.additionalSeats }} extra seat{{
                    appBreakdown.additionalSeats > 1 ? 's' : ''
                  }}
                  × {{ appBreakdown.perSeatPrice | currency }}/seat{{ getBillingPeriodLabel() }}
                </div>
              </div>
              <div class="detailed-cost-amount">{{ appBreakdown.totalCost | currency }}</div>
            </div>
          </ng-container>

          <!-- Additional Seats for Each Addon -->
          <ng-container
            *ngFor="let addonBreakdown of getAdditionalSeatsBreakdownByAddon(); trackBy: trackById"
          >
            <div class="detailed-cost-row">
              <div class="detailed-cost-left">
                <div class="detailed-cost-name">
                  {{ addonBreakdown.addonName }} — Additional Seats
                  <span class="detailed-cost-type-tag tag-addon">Add-on</span>
                </div>
                <div class="detailed-cost-formula">
                  {{ addonBreakdown.additionalSeats }} extra seat{{
                    addonBreakdown.additionalSeats > 1 ? 's' : ''
                  }}
                  × {{ addonBreakdown.perSeatPrice | currency }}/seat{{ getBillingPeriodLabel() }}
                </div>
              </div>
              <div class="detailed-cost-amount">{{ addonBreakdown.totalCost | currency }}</div>
            </div>
          </ng-container>

          <!-- Subtotal (before discount) -->
          <div class="detailed-cost-row" *ngIf="calculateSavingsInformation()">
            <div class="detailed-cost-left">
              <div class="detailed-cost-name">Subtotal</div>
              <div class="detailed-cost-formula">Before discount</div>
            </div>
            <div class="detailed-cost-amount">{{ calculateSubtotal() | currency }}</div>
          </div>

          <!-- Discount (if any) -->
          <div
            class="detailed-cost-row detailed-discount-row"
            *ngIf="calculateSavingsInformation() as savings"
          >
            <div class="detailed-cost-left">
              <div class="detailed-cost-name">
                {{ getBillingCycleDisplayName() }} Discount
                <span class="detailed-cost-type-tag tag-discount">
                  {{ savings.isPercentage ? (savings.savedValue + '% OFF') : ('$' + savings.savedValue.toFixed(2) + ' OFF') }}
                </span>
              </div>
              <div class="detailed-cost-formula">Applied to subtotal</div>
            </div>
            <div class="detailed-cost-amount discount-amount">
              -{{ savings.savedAmount | currency }}
            </div>
          </div>

          <!-- Divider -->
          <div class="detailed-cost-divider"></div>

          <!-- Final Total -->
          <div class="detailed-cost-row detailed-total-row">
            <div class="detailed-cost-left">
              <div class="detailed-cost-name"><strong>Total</strong></div>
              <div class="detailed-cost-formula">Final amount due</div>
            </div>
            <div class="detailed-cost-amount total-amount">
              {{ calculateFinalTotalPrice() | currency }}
              <p class="total-billing-period">
                /{{ currentBillingCycle === BillingCycle.Yearly_PaidYearly ? 'year' : 'month' }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
